import { LogManager } from '@src/utility/Logger';
import { v4 as uuid } from 'uuid';

import { scalar } from '../services/utils';
import { AnimationUtils } from './AnimationTypes';

import type { Animation, Overlay } from './AnimationTypes';
import type { LogoElement } from './elements/LogoElement';
import type { AudioElement } from '@src/editor/models/elements/AudioElement';
import type { CompositionElement } from '@src/editor/models/elements/CompositionElement';
import type { ImageElement } from '@src/editor/models/elements/ImageElement';
import type { ShapeElement } from '@src/editor/models/elements/ShapeElement';
import type { TextElement } from '@src/editor/models/elements/TextElement';
import type { VideoElement } from '@src/editor/models/elements/VideoElement';

const logger = LogManager.getLogger('editor:Element');

/**
 * Default values for the positional properties that RendererSync can reset.
 * When the renderer omits one of these it means the field returned to its
 * default; the external-sync path resets the model to this value (see
 * `resolveResetProps`). Subclasses override `defaultFor` where their defaults
 * differ (TextElement auto-sizes; ShapeElement uses 50% boxes).
 *
 * Keep this in sync with `POSITIONAL_PROPS` (LayoutEngine) — every prop the
 * differ can emit as a reset needs a default here, else the reset degrades to
 * `undefined` and `Element.update` keeps the old value (the PLAT-10700 bug).
 */
export const BASE_POSITIONAL_DEFAULTS: Record<string, string | null> = {
  x: '50%',
  y: '50%',
  width: '100%',
  height: '100%',
  x_scale: '100%',
  y_scale: '100%',
  x_anchor: '50%',
  y_anchor: '50%',
  x_alignment: '50%',
};

/**
 * Base Element class representing any timeline element
 * Aligned with Creatomate properties and structure
 */
export class Element {
  // Private properties
  private _id: string;
  private _name: string | null;
  private _track: number | null; // Creatomate allows null for track
  private _time: number; // Renamed from startTime to match Creatomate
  private _duration: number | string | null; // Creatomate allows null or 'media' value
  private _trim_start: number | null = null; // Creatomate specific property
  private _trim_duration: number | null = null; // Creatomate specific property
  private _type: string;
  private _dynamic: boolean = false;
  private _locked: boolean = false;
  private _visible: boolean = true;
  private _x: string = '50%'; // Default Creatomate relative unit
  private _y: string = '50%';
  protected _width: string | null = '100%'; // Overrideable by subclasses, defaults to null for TextElement
  protected _height: string | null = '100%';
  // private _metadata: Record<string, any> = {};

  // Transform properties
  private _aspect_ratio: number | null = null;
  private _x_padding: string = '0 vw';
  private _y_padding: string = '0 vh';
  private _z_index: number | null = null;
  private _x_anchor: string = '50%';
  private _y_anchor: string = '50%';
  private _x_scale: string = '100%';
  private _y_scale: string = '100%';
  private _x_skew: string = '0';
  private _y_skew: string = '0';
  private _x_rotation: string = '0';
  private _y_rotation: string = '0';
  private _z_rotation: string = '0';
  private _perspective: number | null = null;
  private _backface_visible: boolean = true;

  // Alignment properties
  private _x_alignment: string = '50%';
  private _y_alignment: string = '50%';

  // Fill properties
  private _fill_color: string | null = null;
  private _fill_mode: string = 'solid';
  private _fill_x0: string = '50%';
  private _fill_y0: string = '0%';
  private _fill_x1: string = '50%';
  private _fill_y1: string = '100%';
  private _fill_radius: string = '50%';

  // Stroke properties
  private _stroke_color: string | null = null;
  private _stroke_width: string = '0.1 vmin';
  private _stroke_cap: string = 'round';
  private _stroke_join: string = 'round';
  private _stroke_start: string = '0%';
  private _stroke_end: string = '100%';
  private _stroke_offset: string = '0%';

  // Visual effects properties
  private _border_radius: string = '0 vmin';
  private _shadow_color: string | null = null;
  private _shadow_blur: number = 0;
  private _shadow_x: number = 0;
  private _shadow_y: number = 0;
  private _clip: boolean = false;
  private _opacity: string = '100%';
  private _blend_mode: string = 'none';
  private _color_filter: string = 'none';
  private _color_filter_value: string = '0%';
  private _color_overlay: string | null | Overlay[] = null;
  private _blur_radius: number = 0;
  private _blur_mode: string = 'stack';
  private _mask_mode: string | null = null;
  private _repeat: boolean = false;

  // Advanced properties
  private _warp_mode: string = 'default';
  private _warp_matrix: any[] | null = null;
  private _animations: Animation[] = [];

  // Chroma Key properties
  private _chroma_key_color: string | null = null;
  private _chroma_key_sensitivity: string | null = null;

  /**
   * Transient keyframe overrides for positional properties.
   * When set, toJSON() emits Creatomate keyframe arrays instead of scalar values.
   * Used by LayoutEngine to merge contiguous video chunks with per-scene position changes.
   * Not persisted, not cloned — layout elements are regenerated on every Timeline.toRenderedJSON() / toStoredJSON() call.
   */
  keyframeOverrides: Record<
    string,
    Array<{ time: number; easing: string; value: string }>
  > | null = null;

  constructor(props: Partial<Element> = {}) {
    this._id = props.id ?? uuid();
    this._name = props.name ?? null;
    this._track = props.track ?? null; // Default to null per Creatomate
    // PLAT-11027: heal a negative `time` at the import boundary — `fromJSON`
    // and `clone` both build through here — so a version saved with one loads
    // with the model, the timeline UI and the canvas agreeing, and the next
    // save writes a clean value.
    this._time = this.clampNegativeTime(
      props.time ?? 0, // Support legacy startTime
      'construction',
    );
    this._duration = props.duration ?? null; // Creatomate default
    this._trim_start = props.trim_start ?? null;
    this._trim_duration = props.trim_duration ?? null;

    // Validate type - ensure it's a known type
    const validTypes = [
      'video',
      'text',
      'audio',
      'image',
      'composition',
      'shape',
    ];
    const providedType = props.type ?? '';
    this._type = validTypes.includes(providedType) ? providedType : 'video';

    this._dynamic = props.dynamic ?? false;
    this._locked = props.locked ?? false;
    this._visible = props.visible ?? true;
    this._x = props.x ?? '50%';
    this._y = props.y ?? '50%';
    this._width = props.width ?? '100%';
    this._height = props.height ?? '100%';

    // Transform properties
    this._aspect_ratio = props.aspect_ratio ?? null;
    this._x_padding = props.x_padding ?? '0 vw';
    this._y_padding = props.y_padding ?? '0 vh';
    this._z_index = props.z_index ?? null;
    this._x_anchor = props.x_anchor ?? '50%';
    this._y_anchor = props.y_anchor ?? '50%';
    this._x_scale = props.x_scale ?? '100%';
    this._y_scale = props.y_scale ?? '100%';
    this._x_skew = props.x_skew ?? '0';
    this._y_skew = props.y_skew ?? '0';
    this._x_rotation = props.x_rotation ?? '0';
    this._y_rotation = props.y_rotation ?? '0';
    this._z_rotation = props.z_rotation ?? '0';
    this._perspective = props.perspective ?? null;
    this._backface_visible = props.backface_visible ?? true;

    // Alignment properties
    this._x_alignment = props.x_alignment ?? '50%';
    this._y_alignment = props.y_alignment ?? '50%';

    // Fill properties
    this._fill_color = props.fill_color ?? null;
    this._fill_mode = props.fill_mode ?? 'solid';
    this._fill_x0 = props.fill_x0 ?? '50%';
    this._fill_y0 = props.fill_y0 ?? '0%';
    this._fill_x1 = props.fill_x1 ?? '50%';
    this._fill_y1 = props.fill_y1 ?? '100%';
    this._fill_radius = props.fill_radius ?? '50%';

    // Stroke properties
    this._stroke_color = props.stroke_color ?? null;
    this._stroke_width = props.stroke_width ?? '0.1 vmin';
    this._stroke_cap = props.stroke_cap ?? 'round';
    this._stroke_join = props.stroke_join ?? 'round';
    this._stroke_start = props.stroke_start ?? '0%';
    this._stroke_end = props.stroke_end ?? '100%';
    this._stroke_offset = props.stroke_offset ?? '0%';

    // Visual effects properties
    this._border_radius = props.border_radius ?? '0 vmin';
    this._shadow_color = props.shadow_color ?? null;
    this._shadow_blur = props.shadow_blur ?? 0;
    this._shadow_x = props.shadow_x ?? 0;
    this._shadow_y = props.shadow_y ?? 0;
    this._clip = props.clip ?? false;
    this._opacity = props.opacity ?? '100%';
    this._blend_mode = props.blend_mode ?? 'none';
    this._color_filter = props.color_filter ?? 'none';
    this._color_filter_value = props.color_filter_value ?? '0%';
    this._color_overlay = props.color_overlay ?? null;
    this._blur_radius = props.blur_radius ?? 0;
    this._blur_mode = props.blur_mode ?? 'stack';
    this._mask_mode = props.mask_mode ?? null;
    this._repeat = props.repeat ?? false;

    // Advanced properties
    this._warp_mode = props.warp_mode ?? 'default';
    this._warp_matrix = props.warp_matrix ?? null;
    this._animations = Array.isArray(props.animations)
      ? [...props.animations]
      : [];

    // Chroma Key properties
    this._chroma_key_color = props.chroma_key_color ?? null;
    this._chroma_key_sensitivity = props.chroma_key_sensitivity ?? null;

    // if (props.metadata) {
    //   this._metadata = { ...props.metadata };
    // }
  }

  // Getters
  get id(): string {
    return this._id;
  }

  get name(): string | null {
    return this._name;
  }

  get track(): number | null {
    return this._track;
  }

  get time(): number {
    return this._time;
  }

  get end_time(): number {
    return Number((this._time + this.durationSeconds).toFixed(7));
  }

  get duration(): number | string | null {
    return this._duration;
  }

  get trim_start(): number | null {
    return this._trim_start;
  }

  get durationSeconds(): number {
    if (typeof this._duration === 'string') {
      return parseFloat(this._duration);
    }
    return this._duration != null ? this._duration : 0;
  }

  get trim_duration(): number | null {
    // Really shouldn't use this but it is part of the Creatomate API
    return this._trim_duration;
  }

  get type(): string {
    return this._type;
  }

  get dynamic(): boolean {
    return this._dynamic;
  }

  get locked(): boolean {
    return this._locked;
  }

  get visible(): boolean {
    return this._visible;
  }

  get x(): string {
    return this._x;
  }

  get y(): string {
    return this._y;
  }

  get width(): string | null {
    return this._width;
  }

  get height(): string | null {
    return this._height;
  }

  // Note: Metadata is not supported via Creatomate flow but might want it later?
  // get metadata(): Record<string, any> {
  //   return { ...this._metadata }; // Return a copy to prevent direct mutation
  // }

  // Transform property getters
  get aspect_ratio(): number | null {
    return this._aspect_ratio;
  }

  get x_padding(): string {
    return this._x_padding;
  }

  get y_padding(): string {
    return this._y_padding;
  }

  get z_index(): number | null {
    return this._z_index;
  }

  get x_anchor(): string {
    return this._x_anchor;
  }

  get y_anchor(): string {
    return this._y_anchor;
  }

  get x_scale(): string {
    return this._x_scale;
  }

  get y_scale(): string {
    return this._y_scale;
  }

  get x_skew(): string {
    return this._x_skew;
  }

  get y_skew(): string {
    return this._y_skew;
  }

  get x_rotation(): string {
    return this._x_rotation;
  }

  get y_rotation(): string {
    return this._y_rotation;
  }

  get z_rotation(): string {
    return this._z_rotation;
  }

  get perspective(): number | null {
    return this._perspective;
  }

  get backface_visible(): boolean {
    return this._backface_visible;
  }

  // Alignment property getters
  get x_alignment(): string {
    return this._x_alignment;
  }

  get y_alignment(): string {
    return this._y_alignment;
  }

  // Fill property getters
  get fill_color(): string | null {
    return this._fill_color;
  }

  get fill_mode(): string {
    return this._fill_mode;
  }

  get fill_x0(): string {
    return this._fill_x0;
  }

  get fill_y0(): string {
    return this._fill_y0;
  }

  get fill_x1(): string {
    return this._fill_x1;
  }

  get fill_y1(): string {
    return this._fill_y1;
  }

  get fill_radius(): string {
    return this._fill_radius;
  }

  // Stroke property getters
  get stroke_color(): string | null {
    return this._stroke_color;
  }

  get stroke_width(): string {
    return this._stroke_width;
  }

  get stroke_cap(): string {
    return this._stroke_cap;
  }

  get stroke_join(): string {
    return this._stroke_join;
  }

  get stroke_start(): string {
    return this._stroke_start;
  }

  get stroke_end(): string {
    return this._stroke_end;
  }

  get stroke_offset(): string {
    return this._stroke_offset;
  }

  // Visual effects property getters
  get border_radius(): string {
    return this._border_radius;
  }

  get shadow_color(): string | null {
    return this._shadow_color;
  }

  get shadow_blur(): number {
    return this._shadow_blur;
  }

  get shadow_x(): number {
    return this._shadow_x;
  }

  get shadow_y(): number {
    return this._shadow_y;
  }

  get clip(): boolean {
    return this._clip;
  }

  get opacity(): string {
    return this._opacity;
  }

  get blend_mode(): string {
    return this._blend_mode;
  }

  get color_filter(): string {
    return this._color_filter;
  }

  get color_filter_value(): string {
    return this._color_filter_value;
  }

  get color_overlay(): string | null | Overlay[] {
    return this._color_overlay;
  }

  get blur_radius(): number {
    return this._blur_radius;
  }

  get blur_mode(): string {
    return this._blur_mode;
  }

  get mask_mode(): string | null {
    return this._mask_mode;
  }

  get repeat(): boolean {
    return this._repeat;
  }

  // Advanced property getters
  get warp_mode(): string {
    return this._warp_mode;
  }

  get warp_matrix(): any[] | null {
    return this._warp_matrix;
  }

  get animations(): Animation[] {
    return [...this._animations];
  }

  // Chroma Key property getters
  get chroma_key_color(): string | null {
    return this._chroma_key_color;
  }

  get chroma_key_sensitivity(): string | null {
    return this._chroma_key_sensitivity;
  }

  // Animation methods (migrated from AnimationMixin)

  /**
   * Gets all animations for this element
   */
  getAnimations(): Animation[] {
    return [...this._animations];
  }

  /**
   * Sets the animations array
   */
  setAnimations(animations: Animation[]): void {
    this._animations = animations ? [...animations] : [];
  }

  /**
   * Adds a new animation or replaces existing animation of same type/time
   */
  addAnimation(animation: Animation): void {
    this._animations = AnimationUtils.setAnimation(this._animations, animation);
  }

  /**
   * Removes animation by type and optionally by time
   */
  removeAnimation(type: string, time?: string | number): void {
    this._animations = AnimationUtils.removeAnimation(
      this._animations,
      type,
      time,
    );
  }

  /**
   * Gets enter animation (time: start or 0)
   */
  getEnterAnimation(): Animation | undefined {
    return AnimationUtils.getEnterAnimation(this._animations);
  }

  /**
   * Gets exit animation (time: end)
   */
  getExitAnimation(): Animation | undefined {
    return AnimationUtils.getExitAnimation(this._animations);
  }

  /**
   * Sets enter animation (removes existing enter animation first)
   */
  setEnterAnimation(animation: Animation | null): void {
    // Remove existing enter animations
    this._animations = this._animations.filter(
      anim => anim.time !== 'start' && anim.time !== 0,
    );

    // Add new enter animation if provided
    if (animation) {
      const enterAnimation = {
        ...animation,
        time: animation.time || 'start',
      };

      if (AnimationUtils.validateAnimation(enterAnimation)) {
        this._animations.push(enterAnimation);
      }
    }
  }

  /**
   * Sets exit animation (removes existing exit animation first)
   */
  setExitAnimation(animation: Animation | null): void {
    // Remove existing exit animations
    this._animations = this._animations.filter(anim => anim.time !== 'end');

    // Add new exit animation if provided
    if (animation) {
      const exitAnimation = {
        ...animation,
        time: 'end',
        reversed: true, // Exit animations are typically reversed
      };

      if (AnimationUtils.validateAnimation(exitAnimation)) {
        this._animations.push(exitAnimation);
      }
    }
  }

  /**
   * Creates a basic fade in/out animation pair
   */
  setBasicFadeAnimations(
    enterDuration: number = 1,
    exitDuration: number = 1,
  ): void {
    this.setEnterAnimation(
      AnimationUtils.createFadeAnimation('start', enterDuration, false),
    );
    this.setExitAnimation(
      AnimationUtils.createFadeAnimation('end', exitDuration, true),
    );
  }

  /**
   * Clears all animations
   */
  clearAnimations(): void {
    this._animations = [];
  }

  /**
   * Gets animation by type and time
   */
  getAnimation(type: string, time?: string | number): Animation | undefined {
    return this._animations.find(animation => {
      if (animation.type !== type) {
        return false;
      }
      if (time !== undefined && animation.time !== time) {
        return false;
      }
      return true;
    });
  }

  /**
   * Updates animation properties
   */
  updateAnimation(
    type: string,
    time: string | number | undefined,
    updates: Partial<Animation>,
  ): boolean {
    const animationIndex = this._animations.findIndex(animation => {
      if (animation.type !== type) {
        return false;
      }
      if (time !== undefined && animation.time !== time) {
        return false;
      }
      return true;
    });

    if (animationIndex === -1) {
      return false;
    }

    this._animations[animationIndex] = {
      ...this._animations[animationIndex],
      ...updates,
    };

    return true;
  }

  /**
   * Checks if element has any animations
   */
  hasAnimations(): boolean {
    return this._animations.length > 0;
  }

  /**
   * Checks if element has enter animation
   */
  hasEnterAnimation(): boolean {
    return this.getEnterAnimation() !== undefined;
  }

  /**
   * Checks if element has exit animation
   */
  hasExitAnimation(): boolean {
    return this.getExitAnimation() !== undefined;
  }

  /**
   * Sets keyframe overrides for positional properties.
   * When set, toJSON() emits Creatomate keyframe arrays instead of scalar values.
   * Pass null to clear all overrides.
   */
  setKeyframeOverrides(
    overrides: Record<
      string,
      Array<{ time: number; easing: string; value: string }>
    > | null,
  ): void {
    this.keyframeOverrides = overrides;
  }

  /**
   * The default value for a positional property, used to reset it when the
   * renderer reports it as absent (= returned to its default). Returns
   * `undefined` for properties without a managed default, which callers treat
   * as "leave as-is". Subclasses override where their defaults differ.
   */
  defaultFor(prop: string): string | null | undefined {
    return Object.prototype.hasOwnProperty.call(BASE_POSITIONAL_DEFAULTS, prop)
      ? BASE_POSITIONAL_DEFAULTS[prop]
      : undefined;
  }

  /**
   * Updates element properties
   */
  update(props: Partial<Element>): void {
    // Properties are updated individually to respect the setters/validation
    if (props.id !== undefined) this._id = props.id;
    if (props.name !== undefined) this._name = props.name;
    if (props.track !== undefined) this._track = props.track;
    if (props.time !== undefined) this._time = props.time;
    if (props.duration !== undefined) this._duration = props.duration;
    if (props.trim_start !== undefined) this._trim_start = props.trim_start;
    if (props.trim_duration !== undefined)
      this._trim_duration = props.trim_duration;
    if (props.type !== undefined) {
      // Validate type
      const validTypes = [
        'video',
        'text',
        'audio',
        'image',
        'composition',
        'shape',
      ];
      this._type = validTypes.includes(props.type) ? props.type : this._type;
    }
    if (props.dynamic !== undefined) this._dynamic = props.dynamic;
    if (props.locked !== undefined) this._locked = props.locked;
    if (props.visible !== undefined) this._visible = props.visible;
    if (props.x !== undefined) this._x = props.x;
    if (props.y !== undefined) this._y = props.y;
    if (props.width !== undefined) this._width = props.width;
    if (props.height !== undefined) this._height = props.height;
    // if (props.metadata !== undefined) this._metadata = { ...props.metadata };

    // Transform properties
    if (props.aspect_ratio !== undefined)
      this._aspect_ratio = props.aspect_ratio;
    if (props.x_padding !== undefined) this._x_padding = props.x_padding;
    if (props.y_padding !== undefined) this._y_padding = props.y_padding;
    if (props.z_index !== undefined) this._z_index = props.z_index;
    if (props.x_anchor !== undefined) this._x_anchor = props.x_anchor;
    if (props.y_anchor !== undefined) this._y_anchor = props.y_anchor;
    if (props.x_scale !== undefined) this._x_scale = props.x_scale;
    if (props.y_scale !== undefined) this._y_scale = props.y_scale;
    if (props.x_skew !== undefined) this._x_skew = props.x_skew;
    if (props.y_skew !== undefined) this._y_skew = props.y_skew;
    if (props.x_rotation !== undefined) this._x_rotation = props.x_rotation;
    if (props.y_rotation !== undefined) this._y_rotation = props.y_rotation;
    if (props.z_rotation !== undefined) this._z_rotation = props.z_rotation;
    if (props.perspective !== undefined) this._perspective = props.perspective;
    if (props.backface_visible !== undefined)
      this._backface_visible = props.backface_visible;

    // Shape properties

    // Alignment properties
    if (props.x_alignment !== undefined) this._x_alignment = props.x_alignment;
    if (props.y_alignment !== undefined) this._y_alignment = props.y_alignment;

    // Fill properties
    if (props.fill_color !== undefined) this._fill_color = props.fill_color;
    if (props.fill_mode !== undefined) this._fill_mode = props.fill_mode;
    if (props.fill_x0 !== undefined) this._fill_x0 = props.fill_x0;
    if (props.fill_y0 !== undefined) this._fill_y0 = props.fill_y0;
    if (props.fill_x1 !== undefined) this._fill_x1 = props.fill_x1;
    if (props.fill_y1 !== undefined) this._fill_y1 = props.fill_y1;
    if (props.fill_radius !== undefined) this._fill_radius = props.fill_radius;

    // Stroke properties
    if (props.stroke_color !== undefined)
      this._stroke_color = props.stroke_color;
    if (props.stroke_width !== undefined)
      this._stroke_width = props.stroke_width;
    if (props.stroke_cap !== undefined) this._stroke_cap = props.stroke_cap;
    if (props.stroke_join !== undefined) this._stroke_join = props.stroke_join;
    if (props.stroke_start !== undefined)
      this._stroke_start = props.stroke_start;
    if (props.stroke_end !== undefined) this._stroke_end = props.stroke_end;
    if (props.stroke_offset !== undefined)
      this._stroke_offset = props.stroke_offset;

    // Visual effects properties
    if (props.border_radius !== undefined)
      this._border_radius = props.border_radius;
    if (props.shadow_color !== undefined)
      this._shadow_color = props.shadow_color;
    if (props.shadow_blur !== undefined) this._shadow_blur = props.shadow_blur;
    if (props.shadow_x !== undefined) this._shadow_x = props.shadow_x;
    if (props.shadow_y !== undefined) this._shadow_y = props.shadow_y;
    if (props.clip !== undefined) this._clip = props.clip;
    if (props.opacity !== undefined) this._opacity = props.opacity;
    if (props.blend_mode !== undefined) this._blend_mode = props.blend_mode;
    if (props.color_filter !== undefined)
      this._color_filter = props.color_filter;
    if (props.color_filter_value !== undefined)
      this._color_filter_value = props.color_filter_value;
    if (props.color_overlay !== undefined)
      this._color_overlay = props.color_overlay;
    if (props.blur_radius !== undefined) this._blur_radius = props.blur_radius;
    if (props.blur_mode !== undefined) this._blur_mode = props.blur_mode;
    if (props.mask_mode !== undefined) this._mask_mode = props.mask_mode;
    if (props.repeat !== undefined) this._repeat = props.repeat;

    // Advanced properties
    if (props.warp_mode !== undefined) this._warp_mode = props.warp_mode;
    if (props.warp_matrix !== undefined) this._warp_matrix = props.warp_matrix;
    if (props.animations !== undefined) {
      this._animations = Array.isArray(props.animations)
        ? [...props.animations]
        : [];
    }

    // Chroma Key properties
    if (props.chroma_key_color !== undefined)
      this._chroma_key_color = props.chroma_key_color;
    // validate chroma key sensitivity (must be between 1% and 100%)
    if (props.chroma_key_sensitivity !== undefined) {
      if (props.chroma_key_sensitivity === null) {
        this._chroma_key_sensitivity = null;
      } else if (typeof props.chroma_key_sensitivity === 'string') {
        // intentionally do not handle number input at this time
        // (currently, all values are in the form of strings with % suffix)
        const sensitivity = parseInt(
          props.chroma_key_sensitivity.replace('%', ''),
        );
        if (!isNaN(sensitivity) && sensitivity >= 1 && sensitivity <= 100) {
          this._chroma_key_sensitivity = props.chroma_key_sensitivity;
        }
      }
    }
  }

  /**
   * Updates the time property directly (for internal use only)
   * @internal
   */
  setTime(time: number): void {
    this._time = time;
  }

  /**
   * Updates the duration property directly (for internal use only)
   * @internal
   */
  setDuration(duration: number | string | null): void {
    this._duration = duration;
  }

  /**
   * Updates the id property directly (for internal use only)
   * @internal
   */
  setId(id: string): void {
    this._id = id;
  }

  /**
   * TEMPORARY: Direct access to internal time property for TrackManager compatibility
   * @internal
   * @deprecated Use setTime() instead
   */
  set time(value: number) {
    this._time = value;
  }

  /**
   * TEMPORARY: Direct access to internal duration property for TrackManager compatibility
   * @internal
   * @deprecated Use setDuration() instead
   */
  set duration(value: number | string | null) {
    this._duration = value;
  }

  /**
   * TEMPORARY: Direct access to internal id property for TrackManager compatibility
   * @internal
   * @deprecated Use setId() instead
   */
  set id(value: string) {
    this._id = value;
  }

  set name(value: string | null) {
    this._name = value;
  }

  /**
   * Captures the mutable state of the element.
   * @returns A record of all mutable properties.
   */
  getState(): Record<string, any> {
    return {
      id: this._id,
      name: this._name,
      time: this._time,
      duration: this._duration,
      trim_start: this._trim_start,
      trim_duration: this._trim_duration,
      dynamic: this._dynamic,
      locked: this._locked,
      visible: this._visible,
      x: this._x,
      y: this._y,
      width: this._width,
      height: this._height,
      track: this._track,
      type: this._type,
      // metadata: { ...this._metadata },

      // Transform properties
      aspect_ratio: this._aspect_ratio,
      x_padding: this._x_padding,
      y_padding: this._y_padding,
      z_index: this._z_index,
      x_anchor: this._x_anchor,
      y_anchor: this._y_anchor,
      x_scale: this._x_scale,
      y_scale: this._y_scale,
      x_skew: this._x_skew,
      y_skew: this._y_skew,
      x_rotation: this._x_rotation,
      y_rotation: this._y_rotation,
      z_rotation: this._z_rotation,
      perspective: this._perspective,
      backface_visible: this._backface_visible,
      // Alignment properties
      x_alignment: this._x_alignment,
      y_alignment: this._y_alignment,
      // Fill properties
      fill_color: this._fill_color,
      fill_mode: this._fill_mode,
      fill_x0: this._fill_x0,
      fill_y0: this._fill_y0,
      fill_x1: this._fill_x1,
      fill_y1: this._fill_y1,
      fill_radius: this._fill_radius,
      // Stroke properties
      stroke_color: this._stroke_color,
      stroke_width: this._stroke_width,
      stroke_cap: this._stroke_cap,
      stroke_join: this._stroke_join,
      stroke_start: this._stroke_start,
      stroke_end: this._stroke_end,
      stroke_offset: this._stroke_offset,
      // Visual effects properties
      border_radius: this._border_radius,
      shadow_color: this._shadow_color,
      shadow_blur: this._shadow_blur,
      shadow_x: this._shadow_x,
      shadow_y: this._shadow_y,
      clip: this._clip,
      opacity: this._opacity,
      blend_mode: this._blend_mode,
      color_filter: this._color_filter,
      color_filter_value: this._color_filter_value,
      color_overlay: this._color_overlay,
      blur_radius: this._blur_radius,
      blur_mode: this._blur_mode,
      mask_mode: this._mask_mode,
      repeat: this._repeat,
      // Advanced properties
      warp_mode: this._warp_mode,
      warp_matrix: this._warp_matrix,
      animations: [...this._animations],
      // Chroma Key properties
      chroma_key_color: this._chroma_key_color,
      chroma_key_sensitivity: this._chroma_key_sensitivity,
    };
  }

  /**
   * Restores the element's mutable state from a state record.
   * @param state The state to restore.
   */
  restoreState(state: Record<string, any>): void {
    if (state.time !== undefined) this._time = state.time;
    if (state.duration !== undefined) this._duration = state.duration;
    if (state.trim_start !== undefined) this._trim_start = state.trim_start;
    if (state.trim_duration !== undefined)
      this._trim_duration = state.trim_duration;
    if (state.dynamic !== undefined) this._dynamic = state.dynamic;
    if (state.locked !== undefined) this._locked = state.locked;
    if (state.visible !== undefined) this._visible = state.visible;
    if (state.x !== undefined) this._x = state.x;
    if (state.y !== undefined) this._y = state.y;
    if (state.width !== undefined) this._width = state.width;
    if (state.height !== undefined) this._height = state.height;
    if (state.track !== undefined) this._track = state.track;
    if (state.type !== undefined && this._type !== state.type) {
      // Only update type if it's a valid type and different from current
      const validTypes = [
        'video',
        'text',
        'audio',
        'image',
        'composition',
        'shape',
      ];
      this._type = validTypes.includes(state.type) ? state.type : this._type;
    }
    // if (state.metadata !== undefined) this._metadata = { ...state.metadata };

    // Transform properties
    if (state.aspect_ratio !== undefined)
      this._aspect_ratio = state.aspect_ratio;
    if (state.x_padding !== undefined) this._x_padding = state.x_padding;
    if (state.y_padding !== undefined) this._y_padding = state.y_padding;
    if (state.z_index !== undefined) this._z_index = state.z_index;
    if (state.x_anchor !== undefined) this._x_anchor = state.x_anchor;
    if (state.y_anchor !== undefined) this._y_anchor = state.y_anchor;
    if (state.x_scale !== undefined) this._x_scale = state.x_scale;
    if (state.y_scale !== undefined) this._y_scale = state.y_scale;
    if (state.x_skew !== undefined) this._x_skew = state.x_skew;
    if (state.y_skew !== undefined) this._y_skew = state.y_skew;
    if (state.x_rotation !== undefined) this._x_rotation = state.x_rotation;
    if (state.y_rotation !== undefined) this._y_rotation = state.y_rotation;
    if (state.z_rotation !== undefined) this._z_rotation = state.z_rotation;
    if (state.perspective !== undefined) this._perspective = state.perspective;
    if (state.backface_visible !== undefined)
      this._backface_visible = state.backface_visible;

    // Alignment properties
    if (state.x_alignment !== undefined) this._x_alignment = state.x_alignment;
    if (state.y_alignment !== undefined) this._y_alignment = state.y_alignment;

    // Fill properties
    if (state.fill_color !== undefined) this._fill_color = state.fill_color;
    if (state.fill_mode !== undefined) this._fill_mode = state.fill_mode;
    if (state.fill_x0 !== undefined) this._fill_x0 = state.fill_x0;
    if (state.fill_y0 !== undefined) this._fill_y0 = state.fill_y0;
    if (state.fill_x1 !== undefined) this._fill_x1 = state.fill_x1;
    if (state.fill_y1 !== undefined) this._fill_y1 = state.fill_y1;
    if (state.fill_radius !== undefined) this._fill_radius = state.fill_radius;

    // Stroke properties
    if (state.stroke_color !== undefined)
      this._stroke_color = state.stroke_color;
    if (state.stroke_width !== undefined)
      this._stroke_width = state.stroke_width;
    if (state.stroke_cap !== undefined) this._stroke_cap = state.stroke_cap;
    if (state.stroke_join !== undefined) this._stroke_join = state.stroke_join;
    if (state.stroke_start !== undefined)
      this._stroke_start = state.stroke_start;
    if (state.stroke_end !== undefined) this._stroke_end = state.stroke_end;
    if (state.stroke_offset !== undefined)
      this._stroke_offset = state.stroke_offset;

    // Visual effects properties
    if (state.border_radius !== undefined)
      this._border_radius = state.border_radius;
    if (state.shadow_color !== undefined)
      this._shadow_color = state.shadow_color;
    if (state.shadow_blur !== undefined) this._shadow_blur = state.shadow_blur;
    if (state.shadow_x !== undefined) this._shadow_x = state.shadow_x;
    if (state.shadow_y !== undefined) this._shadow_y = state.shadow_y;
    if (state.clip !== undefined) this._clip = state.clip;
    if (state.opacity !== undefined) this._opacity = state.opacity;
    if (state.blend_mode !== undefined) this._blend_mode = state.blend_mode;
    if (state.color_filter !== undefined)
      this._color_filter = state.color_filter;
    if (state.color_filter_value !== undefined)
      this._color_filter_value = state.color_filter_value;
    if (state.color_overlay !== undefined)
      this._color_overlay = state.color_overlay;
    if (state.blur_radius !== undefined) this._blur_radius = state.blur_radius;
    if (state.blur_mode !== undefined) this._blur_mode = state.blur_mode;
    if (state.mask_mode !== undefined) this._mask_mode = state.mask_mode;
    if (state.repeat !== undefined) this._repeat = state.repeat;

    // Advanced properties
    if (state.warp_mode !== undefined) this._warp_mode = state.warp_mode;
    if (state.warp_matrix !== undefined) this._warp_matrix = state.warp_matrix;
    if (state.animations !== undefined) {
      this._animations = Array.isArray(state.animations)
        ? [...state.animations]
        : [];
    }

    // Chroma Key properties
    if (state.chroma_key_color !== undefined)
      this._chroma_key_color = state.chroma_key_color;
    if (state.chroma_key_sensitivity !== undefined)
      this._chroma_key_sensitivity = state.chroma_key_sensitivity;
  }

  /**
   * Creates a deep clone of this element
   */
  clone(): Element {
    return new Element({
      id: this._id,
      name: this._name,
      track: this._track,
      time: this._time,
      duration: this._duration,
      trim_start: this._trim_start,
      trim_duration: this._trim_duration,
      type: this._type,
      dynamic: this._dynamic,
      locked: this._locked,
      visible: this._visible,
      x: this._x,
      y: this._y,
      width: this._width,
      height: this._height,
      // metadata: { ...this._metadata },
      // Transform properties
      aspect_ratio: this._aspect_ratio,
      x_padding: this._x_padding,
      y_padding: this._y_padding,
      z_index: this._z_index,
      x_anchor: this._x_anchor,
      y_anchor: this._y_anchor,
      x_scale: this._x_scale,
      y_scale: this._y_scale,
      x_skew: this._x_skew,
      y_skew: this._y_skew,
      x_rotation: this._x_rotation,
      y_rotation: this._y_rotation,
      z_rotation: this._z_rotation,
      perspective: this._perspective,
      backface_visible: this._backface_visible,
      // Alignment properties
      x_alignment: this._x_alignment,
      y_alignment: this._y_alignment,
      // Fill properties
      fill_color: this._fill_color,
      fill_mode: this._fill_mode,
      fill_x0: this._fill_x0,
      fill_y0: this._fill_y0,
      fill_x1: this._fill_x1,
      fill_y1: this._fill_y1,
      fill_radius: this._fill_radius,
      // Stroke properties
      stroke_color: this._stroke_color,
      stroke_width: this._stroke_width,
      stroke_cap: this._stroke_cap,
      stroke_join: this._stroke_join,
      stroke_start: this._stroke_start,
      stroke_end: this._stroke_end,
      stroke_offset: this._stroke_offset,
      // Visual effects properties
      border_radius: this._border_radius,
      shadow_color: this._shadow_color,
      shadow_blur: this._shadow_blur,
      shadow_x: this._shadow_x,
      shadow_y: this._shadow_y,
      clip: this._clip,
      opacity: this._opacity,
      blend_mode: this._blend_mode,
      color_filter: this._color_filter,
      color_filter_value: this._color_filter_value,
      color_overlay: this._color_overlay,
      blur_radius: this._blur_radius,
      blur_mode: this._blur_mode,
      mask_mode: this._mask_mode,
      repeat: this._repeat,
      // Advanced properties
      warp_mode: this._warp_mode,
      warp_matrix: this._warp_matrix,
      animations: [...this._animations],
      // Chroma Key properties
      chroma_key_color: this._chroma_key_color,
      chroma_key_sensitivity: this._chroma_key_sensitivity,
    });
  }

  /**
   * Converts the element to JSON compatible with Creatomate
   */
  toJSON(formatTime: Boolean = false): Record<string, any> {
    const time = this.clampNegativeTime(this._time, 'serialize');
    const json: Record<string, any> = {
      id: this._id,
      name: this._name,
      track: this._track,
      // Format time properties to Creatomate's expected format
      time: formatTime ? this.formatTimeProperty(time) : time,
      duration: formatTime
        ? this.formatTimeProperty(this._duration)
        : this._duration,
      type: this._type,
      dynamic: this._dynamic,
      locked: this._locked,
      visible: this._visible,
      x: this._x,
      y: this._y,
      width: this._width,
      height: this._height,
      // Transform properties (only include non-default values)
      ...(this._aspect_ratio !== null && { aspect_ratio: this._aspect_ratio }),
      ...(this._x_padding !== '0 vw' && { x_padding: this._x_padding }),
      ...(this._y_padding !== '0 vh' && { y_padding: this._y_padding }),
      ...(this._z_index !== null && { z_index: this._z_index }),
      ...(this._x_anchor !== '50%' && { x_anchor: this._x_anchor }),
      ...(this._y_anchor !== '50%' && { y_anchor: this._y_anchor }),
      ...(this._x_scale !== '100%' && { x_scale: this._x_scale }),
      ...(this._y_scale !== '100%' && { y_scale: this._y_scale }),
      ...(this._x_skew !== '0' && { x_skew: this._x_skew }),
      ...(this._y_skew !== '0' && { y_skew: this._y_skew }),
      ...(this._x_rotation !== '0' && { x_rotation: this._x_rotation }),
      ...(this._y_rotation !== '0' && { y_rotation: this._y_rotation }),
      ...(this._z_rotation !== '0' && { z_rotation: this._z_rotation }),
      ...(this._perspective !== null && { perspective: this._perspective }),
      ...(this._backface_visible !== true && {
        backface_visible: this._backface_visible,
      }),
      // Alignment properties (only include non-default values)
      ...(this._x_alignment !== '50%' && { x_alignment: this._x_alignment }),
      ...(this._y_alignment !== '50%' && { y_alignment: this._y_alignment }),
      // Fill properties (only include non-default values)
      ...(this._fill_color !== null && { fill_color: this._fill_color }),
      ...(this._fill_mode !== 'solid' && { fill_mode: this._fill_mode }),
      ...(this._fill_x0 !== '50%' && { fill_x0: this._fill_x0 }),
      ...(this._fill_y0 !== '0%' && { fill_y0: this._fill_y0 }),
      ...(this._fill_x1 !== '50%' && { fill_x1: this._fill_x1 }),
      ...(this._fill_y1 !== '100%' && { fill_y1: this._fill_y1 }),
      ...(this._fill_radius !== '50%' && { fill_radius: this._fill_radius }),
      // Stroke properties (only include non-default values)
      ...(this._stroke_color !== null && { stroke_color: this._stroke_color }),
      ...(this._stroke_width !== '0.1 vmin' && {
        stroke_width: this._stroke_width,
      }),
      ...(this._stroke_cap !== 'round' && { stroke_cap: this._stroke_cap }),
      ...(this._stroke_join !== 'round' && { stroke_join: this._stroke_join }),
      ...(this._stroke_start !== '0%' && { stroke_start: this._stroke_start }),
      ...(this._stroke_end !== '100%' && { stroke_end: this._stroke_end }),
      ...(this._stroke_offset !== '0%' && {
        stroke_offset: this._stroke_offset,
      }),
      // Visual effects properties (only include non-default values)
      ...(this._border_radius !== '0 vmin' && {
        border_radius: this._border_radius,
      }),
      ...(this._shadow_color !== null && { shadow_color: this._shadow_color }),
      ...(this._shadow_blur !== 0 && { shadow_blur: this._shadow_blur }),
      ...(this._shadow_x !== 0 && { shadow_x: this._shadow_x }),
      ...(this._shadow_y !== 0 && { shadow_y: this._shadow_y }),
      ...(this._clip !== false && { clip: this._clip }),
      ...(this._opacity !== '100%' && { opacity: this._opacity }),
      ...(this._blend_mode !== 'none' && { blend_mode: this._blend_mode }),
      ...(this._color_filter !== 'none' && {
        color_filter: this._color_filter,
      }),
      ...(this._color_filter_value !== '0%' && {
        color_filter_value: this._color_filter_value,
      }),
      ...(this._color_overlay !== null && {
        color_overlay: this._color_overlay,
      }),
      ...(this._blur_radius !== 0 && { blur_radius: this._blur_radius }),
      ...(this._blur_mode !== 'stack' && { blur_mode: this._blur_mode }),
      ...(this._mask_mode !== null && { mask_mode: this._mask_mode }),
      ...(this._repeat !== false && { repeat: this._repeat }),
      // Advanced properties (only include non-default values)
      ...(this._warp_mode !== 'default' && { warp_mode: this._warp_mode }),
      ...(this._warp_matrix !== null && { warp_matrix: this._warp_matrix }),
      ...(this._animations.length > 0 && { animations: [...this._animations] }),
      // Chroma Key properties
      ...(this._chroma_key_color !== null && {
        chroma_key_color: this._chroma_key_color,
      }),
      ...(this._chroma_key_sensitivity !== null && {
        chroma_key_sensitivity: this._chroma_key_sensitivity,
      }),
      // Additional properties from subclasses
      ...this.getAdditionalProperties(),
    };

    // Apply keyframe overrides — replaces scalar values with Creatomate keyframe arrays
    // for properties that vary across merged video chunks (e.g. x, y, width, height)
    if (this.keyframeOverrides) {
      for (const [key, keyframes] of Object.entries(this.keyframeOverrides)) {
        if (keyframes.length > 0) {
          json[key] = keyframes;
        }
      }
    }

    // Only include trim properties if they are non-null
    if (this._trim_start !== null) {
      json.trim_start = formatTime
        ? this.formatTimeProperty(this._trim_start)
        : this._trim_start;
    }

    if (this._trim_duration !== null) {
      json.trim_duration = formatTime
        ? this.formatTimeProperty(this._trim_duration)
        : this._trim_duration;
    }

    return json;
  }

  /**
   * PLAT-11027: guarantees the `time >= 0` invariant on everything the model
   * emits. Creatomate rejects a negative time with `Composition.time: Expected
   * a value of at least 0`, and the rejection discards the *whole* source —
   * blanking the preview canvas, or failing the render on publish — rather than
   * misplacing the one element.
   *
   * Enforced on `toJSON` because every serializer (`toPreviewJSON` /
   * `toStoredJSON` / `toRenderJSON`), every nested composition child and every
   * layout-derived element passes through it, and on construction to heal
   * already-persisted values. A negative value still means placement math wrote
   * one (the swap path clamps the dragged element but not the ones it
   * displaces), so warn rather than clamp silently — `construction` means a
   * saved version carried one in, `serialize` means this session produced one.
   */
  private clampNegativeTime(
    time: number,
    phase: 'construction' | 'serialize',
  ): number {
    if (!(time < 0)) return time;

    logger.warn('Clamped negative element time', {
      id: this._id,
      originalTime: time,
      phase,
    });
    return 0;
  }

  /**
   * Formats time properties for Creatomate compatibility
   * Time-based properties in Creatomate can be:
   * - Number (interpreted as seconds)
   * - String with units (e.g., "5 s")
   * - Special values like "media" for duration
   */
  protected formatTimeProperty(
    value: number | string | null,
  ): number | string | null {
    if (value === null || value === undefined) {
      return null;
    }

    // Special case for 'media' string value in duration
    if (value === 'media') {
      return 'media';
    }

    // If it's already a string and looks like a valid time format, return as is
    if (typeof value === 'string') {
      if (value.match(/^\d+(\.\d+)?\s*s$/)) {
        return value; // Already in the format "X s"
      }

      // Try to parse the string to a number
      const num = parseFloat(value);
      if (!isNaN(num)) {
        return `${num} s`;
      }

      // If we can't parse it, return as is (might be another special value)
      return value;
    }

    // If it's a number, format it as "X s"
    if (typeof value === 'number') {
      return `${value} s`;
    }

    // Fallback - return as is
    return value;
  }

  /**
   * Returns additional properties for JSON serialization
   * To be overridden by subclasses
   */
  protected getAdditionalProperties(): Record<string, any> {
    // return { ...this._metadata };
    return {};
  }

  /**
   * Creates an Element from JSON data
   */
  static fromJSON(json: Record<string, any>): Element {
    // Parse time-based properties that may be formatted as strings
    const parsedTime = Element.parseTimeProperty(
      json.time ?? json.start ?? json.globalTime ?? 0,
    );
    const parsedDuration = Element.parseTimeProperty(json.duration);
    const parsedTrimStart = Element.parseTimeProperty(json.trim_start);
    const parsedTrimDuration = Element.parseTimeProperty(json.trim_duration);

    // Ensure parsedTime is a number, default to 0 if null
    const timeValue = parsedTime !== null ? Number(parsedTime) : 0;

    // Ensure trim values are properly typed for the constructor
    const trimStart =
      typeof parsedTrimStart === 'string'
        ? Number(parsedTrimStart)
        : parsedTrimStart;

    const trimDuration =
      typeof parsedTrimDuration === 'string'
        ? Number(parsedTrimDuration)
        : parsedTrimDuration;

    return new Element({
      id: json.id,
      track: json.track ?? null,
      time: timeValue, // Support legacy properties
      duration: parsedDuration,
      trim_start: trimStart as number | null,
      trim_duration: trimDuration as number | null,
      type: json.type ?? 'video',
      dynamic: json.dynamic ?? false,
      locked: json.locked ?? false,
      visible: json.visible ?? true,
      x: scalar(json.x, '50%'),
      y: scalar(json.y, '50%'),
      width: scalar(json.width, '100%'),
      height: scalar(json.height, '100%'),
      // Transform properties
      aspect_ratio: json.aspect_ratio ?? null,
      x_padding: scalar(json.x_padding, '0 vw'),
      y_padding: scalar(json.y_padding, '0 vh'),
      z_index: json.z_index ?? null,
      x_anchor: scalar(json.x_anchor, '50%'),
      y_anchor: scalar(json.y_anchor, '50%'),
      x_scale: scalar(json.x_scale, '100%'),
      y_scale: scalar(json.y_scale, '100%'),
      x_skew: scalar(json.x_skew, '0'),
      y_skew: scalar(json.y_skew, '0'),
      x_rotation: scalar(json.x_rotation, '0'),
      y_rotation: scalar(json.y_rotation, '0'),
      z_rotation: scalar(json.z_rotation, '0'),
      perspective: json.perspective ?? null,
      backface_visible: json.backface_visible ?? true,
      // Alignment properties
      x_alignment: json.x_alignment ?? '50%',
      y_alignment: json.y_alignment ?? '50%',
      // Fill properties
      fill_color: json.fill_color ?? null,
      fill_mode: json.fill_mode ?? 'solid',
      fill_x0: json.fill_x0 ?? '50%',
      fill_y0: json.fill_y0 ?? '0%',
      fill_x1: json.fill_x1 ?? '50%',
      fill_y1: json.fill_y1 ?? '100%',
      fill_radius: json.fill_radius ?? '50%',
      // Stroke properties
      stroke_color: json.stroke_color ?? null,
      stroke_width: json.stroke_width ?? '0.1 vmin',
      stroke_cap: json.stroke_cap ?? 'round',
      stroke_join: json.stroke_join ?? 'round',
      stroke_start: json.stroke_start ?? '0%',
      stroke_end: json.stroke_end ?? '100%',
      stroke_offset: json.stroke_offset ?? '0%',
      // Visual effects properties
      border_radius: json.border_radius ?? '0 vmin',
      shadow_color: json.shadow_color ?? null,
      shadow_blur: json.shadow_blur ?? 0,
      shadow_x: json.shadow_x ?? 0,
      shadow_y: json.shadow_y ?? 0,
      clip: json.clip ?? false,
      opacity: json.opacity ?? '100%',
      blend_mode: json.blend_mode ?? 'none',
      color_filter: json.color_filter ?? 'none',
      color_filter_value: json.color_filter_value ?? '0%',
      color_overlay: json.color_overlay ?? null,
      blur_radius: json.blur_radius ?? 0,
      blur_mode: json.blur_mode ?? 'stack',
      mask_mode: json.mask_mode ?? null,
      repeat: json.repeat ?? false,
      // Advanced properties
      warp_mode: json.warp_mode ?? 'default',
      warp_matrix: json.warp_matrix ?? null,
      animations: Array.isArray(json.animations) ? json.animations : [],
      // Chroma Key properties
      chroma_key_color: json.chroma_key_color ?? null,
      chroma_key_sensitivity: json.chroma_key_sensitivity ?? null,
      // metadata: { ...json },
    });
  }

  /**
   * Parses a time property from Creatomate format to our internal format
   */
  static parseTimeProperty(
    value: number | string | null | undefined,
  ): number | string | null {
    if (value === null || value === undefined) {
      return null;
    }

    // Special case for 'media' string in duration
    if (value === 'media') {
      return 'media';
    }

    // If it's a string with time format like "5 s", parse it to a number
    if (typeof value === 'string') {
      // Check if it matches the format "X s"
      const match = value.match(/^(\d+(\.\d+)?)\s*s$/);
      if (match) {
        return parseFloat(match[1]);
      }

      // Try to parse as a regular number
      const num = parseFloat(value);
      if (!isNaN(num)) {
        return num;
      }

      // If we can't parse it, return as is (might be another special value)
      return value;
    }

    // If it's already a number, return it directly
    return value;
  }

  // Type predicate functions for narrowing Element types

  /**
   * Type predicate to check if an element is a VideoElement
   */
  static isVideoElement(element: Element | undefined): element is VideoElement {
    return element?.type === 'video';
  }

  /**
   * Type predicate to check if an element is a TextElement
   */

  static isTextElement(element: Element | undefined): element is TextElement {
    return element?.type === 'text';
  }

  /**
   * Type predicate to check if an element is an AudioElement
   */

  static isAudioElement(element: Element | undefined): element is AudioElement {
    return element?.type === 'audio';
  }

  /**
   * Type predicate to check if an element is an ImageElement
   */

  static isImageElement(element: Element | undefined): element is ImageElement {
    return element?.type === 'image';
  }

  /**
   * Type predicate to check if an element is a LogoElement
   */
  static isLogoElement(element: Element | undefined): element is LogoElement {
    // cannot use instanceof here due to circular dependency
    return !!(
      element as Element & { isLogoElement?: () => boolean }
    )?.isLogoElement?.();
  }

  /**
   * Type predicate to check if an element is a CompositionElement
   */

  static isCompositionElement(
    element: Element | undefined,
  ): element is CompositionElement {
    return element?.type === 'composition';
  }

  static isImageCompositionElement(
    element: Element | undefined,
  ): element is CompositionElement {
    // Definition: exactly two child elements: one image and one shape (order agnostic).
    if (!this.isCompositionElement(element)) return false;
    const children = element.elements; // narrowed to CompositionElement
    if (!Array.isArray(children) || children.length !== 2) return false;
    const imageCount = children.filter(e => this.isImageElement(e)).length;
    const shapeCount = children.filter(e => this.isShapeElement(e)).length;
    return imageCount === 1 && shapeCount === 1;
  }

  /**
   * Type predicate to check if an element is a ShapeElement
   */

  static isShapeElement(element: Element | undefined): element is ShapeElement {
    return element?.type === 'shape';
  }

  /**
   * Outputs prefix + unique id
   */
  static newKey(prefix: string): string {
    return `${prefix}-${Date.now()}-${Math.random()
      .toString(36)
      .substring(2, 7)}`;
  }

  /**
   * Strips visual enter/exit animations from a seam (interior) edge created by a
   * split/edit so the new internal boundary is a clean hard cut. Enter animations
   * are keyed at `'start'`/`0`, exit animations at `'end'`. Audio fades are out of
   * scope and intentionally left untouched.
   *
   * @param element  The element whose seam-edge animation should be removed.
   * @param which    `enter: true` strips the enter animation; `exit: true`
   *                 strips the exit animation.
   */
  static stripSeamAnimations(
    element: Element,
    which: { enter?: boolean; exit?: boolean },
  ): void {
    if (which.enter) {
      element.removeAnimation('', 'start');
      element.removeAnimation('', 0);
    }
    if (which.exit) {
      element.removeAnimation('', 'end');
    }
  }
}
